import { useState, type CSSProperties } from 'react';
import { bffFetch, BffError } from '@aosenuma/lib-supabase';
import { parseLayerFile } from './parseLayerFile';
import type { SimpleGeometry } from './parseUploadedGeoJSON';

/**
 * Dropzone GeoJSON para cargar un predio privado (no-RAN).
 *
 * Flujo:
 *   1. Drag/drop archivo .geojson o .json (o "Seleccionar" botón fallback).
 *   2. Parse client-side: tipo, bounds México, normalización a Polygon/MultiPolygon.
 *      Errores claros sin tocar BFF.
 *   3. Mini-form: municipio (Huimilpan/Corregidora), nom_nucleo opcional,
 *      num_parcela opcional.
 *   4. Submit → POST /v1/captura/parcela-privada → BFF llama RPC
 *      captura_insert_parcela_privada en Supabase.
 *   5. On success: `onParcelaCreated(parcela_id)` para que CapturaPage
 *      setee parcelaId y continúe el wizard. Warnings de overlap ≤1% se
 *      muestran inline pero NO bloquean.
 *
 * Handoff parcelas privadas v2 §8 / Fase 4.
 */

/**
 * Severidad del warning de overlap devuelto por el backend.
 * - `minor`: ≤ 1% de solape, tolerable en ambos flujos (insert y replace).
 * - `overlap_exceeded`: > 1% de solape. En insert (0057) esto BLOQUEA con
 *   exception; en replace (0078) llega como warning porque el técnico ya
 *   decidió que su nuevo polígono es el correcto.
 * - `contains_existing`: el nuevo polígono contiene completamente a otra
 *   parcela. En insert BLOQUEA; en replace llega como warning.
 */
type WarningKind = 'minor' | 'overlap_exceeded' | 'contains_existing';

interface OverlapWarning {
  source: string;
  parcela_id: string;
  overlap_pct: number;
  /** Presente solo en respuestas del replace (0078). En insert (0057) no
   *  viene porque los warnings ahí son siempre menores (los graves bloquean). */
  kind?: WarningKind;
}

interface PrivadaResponse {
  id: string;
  parcela_id: string;
  area_ha: number;
  warnings: OverlapWarning[];
}

interface ReplaceResponse extends PrivadaResponse {
  area_ha_previous: number;
}

/**
 * Datos enviados al callback tras crear exitosamente. Incluye lo
 * suficiente para que CapturaPage construya un `sigMeta` sintético y
 * autocomplete los inputs derivados (superficie en Términos económicos,
 * núcleo en el header, etc.) sin requerir click en mapa — la privada
 * no aparece en el mapa de Captura todavía (Fase 5 solo aplicó merge
 * en dashboard cliente).
 */
export interface ParcelaPrivadaCreatedMeta {
  parcelaId: string;
  areaHa: number;
  municipio: string;
  nomNucleo: string | null;
  numParcela: string | null;
  /** Centroide aproximado del polígono (Polygon.coordinates[0] promedio). */
  lng?: number;
  lat?: number;
}

/**
 * Modo del componente. `create` (default) = flujo original con dropzone +
 * mini-form (municipio/nom_nucleo/num_parcela) + POST. `replace` = solo
 * dropzone + PUT al parcela_id existente, preservando metadata.
 */
export type UploadParcelaPrivadaMode =
  | { kind: 'create' }
  | {
      kind: 'replace';
      /** PRV-XXX-##### existente cuyo polígono se va a reemplazar. */
      parcelaId: string;
      /** Area_ha actual (mostrada en el banner pre-replace). */
      areaHaPrevia?: number;
      /** Llamado cuando el técnico cancela el flujo de replace. */
      onCancel?: () => void;
      /** Llamado al reemplazar OK (recibe meta con la nueva areaHa). */
      onParcelaReplaced?: (meta: ParcelaPrivadaCreatedMeta & { areaHaPrevious: number }) => void;
    };

interface Props {
  /** Llamado tras crear exitosamente (solo en modo `create`). */
  onParcelaCreated: (meta: ParcelaPrivadaCreatedMeta) => void;
  /** Modo del componente. Si se omite, equivale a `{ kind: 'create' }`. */
  mode?: UploadParcelaPrivadaMode;
}

/** Centroide aproximado (promedio simple de vertices del primer ring). */
function approxCentroid(geom: SimpleGeometry): { lng?: number; lat?: number } {
  const ring = geom.type === 'Polygon' ? geom.coordinates[0] : geom.coordinates[0]?.[0];
  if (!Array.isArray(ring) || ring.length === 0) return {};
  let sumLng = 0;
  let sumLat = 0;
  let n = 0;
  for (const pt of ring) {
    if (Array.isArray(pt) && typeof pt[0] === 'number' && typeof pt[1] === 'number') {
      sumLng += pt[0];
      sumLat += pt[1];
      n++;
    }
  }
  return n > 0 ? { lng: sumLng / n, lat: sumLat / n } : {};
}

type FileState =
  | { kind: 'idle' }
  | { kind: 'parsed'; geom: SimpleGeometry; sourceRaw: unknown; fileName: string; warnings: string[] }
  | { kind: 'error'; message: string };

const MUNICIPIOS = ['HUIMILPAN', 'CORREGIDORA'] as const;
type Municipio = (typeof MUNICIPIOS)[number] | '';

export function UploadParcelaPrivada({ onParcelaCreated, mode }: Props) {
  const modeKind = mode?.kind ?? 'create';
  const isReplace = modeKind === 'replace';
  const [drag, setDrag] = useState(false);
  const [fileState, setFileState] = useState<FileState>({ kind: 'idle' });
  const [municipio, setMunicipio] = useState<Municipio>('');
  const [nomNucleo, setNomNucleo] = useState('');
  const [numParcela, setNumParcela] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successWarnings, setSuccessWarnings] = useState<PrivadaResponse['warnings']>([]);

  async function handleFile(file: File | null | undefined) {
    setSubmitError(null);
    setSuccessWarnings([]);
    if (!file) return;
    const nameLow = file.name.toLowerCase();
    if (!/\.(geojson|json|kml|kmz)$/.test(nameLow)) {
      setFileState({ kind: 'error', message: 'Formatos soportados: .geojson, .json, .kml, .kmz' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFileState({ kind: 'error', message: 'Archivo > 5 MB' });
      return;
    }
    // parseLayerFile normaliza GeoJSON / KML / KMZ a geometría WGS84 (mismo
    // motor que Carga masiva). Este flujo es single-polígono: si el archivo
    // trae varios, toma el primero y avisa que use Carga masiva para el resto.
    const result = await parseLayerFile(file);
    if (!result.ok) {
      setFileState({ kind: 'error', message: result.error });
      return;
    }
    const first = result.items[0];
    if (!first) {
      setFileState({ kind: 'error', message: 'El archivo no contiene un polígono.' });
      return;
    }
    const warnings = [...result.warnings];
    if (result.items.length > 1) {
      warnings.unshift(
        `El archivo trae ${result.items.length} polígonos; se cargó solo el primero. Para varios usa "Carga masiva".`,
      );
    }
    setFileState({
      kind: 'parsed',
      geom: first.geojson_geom,
      sourceRaw: first.geojson_raw,
      fileName: file.name,
      warnings,
    });
  }

  async function handleSubmit() {
    if (fileState.kind !== 'parsed') return;
    if (mode?.kind !== 'replace' && !municipio) return;
    setSubmitting(true);
    setSubmitError(null);
    setSuccessWarnings([]);
    try {
      if (mode?.kind === 'replace') {
        // PUT: solo reemplaza geom + source_raw; preserva municipio/nom_nucleo/
        // num_parcela del registro original. El RPC del backend recalcula
        // area_ha desde el trigger pp_set_area_ha.
        const response = await bffFetch<ReplaceResponse>(
          `/v1/captura/parcela-privada/${encodeURIComponent(mode.parcelaId)}`,
          {
            method: 'PUT',
            body: {
              geom: fileState.geom,
              source_raw: fileState.sourceRaw,
            },
          },
        );
        setSuccessWarnings(response.warnings ?? []);
        const centroid = approxCentroid(fileState.geom);
        mode.onParcelaReplaced?.({
          parcelaId: response.parcela_id,
          areaHa: response.area_ha,
          areaHaPrevious: response.area_ha_previous,
          // Metadata original — el caller la conoce, este componente NO la
          // re-emite en replace porque no la editó.
          municipio: '',
          nomNucleo: null,
          numParcela: null,
          lng: centroid.lng,
          lat: centroid.lat,
        });
      } else {
        const response = await bffFetch<PrivadaResponse>('/v1/captura/parcela-privada', {
          method: 'POST',
          body: {
            municipio,
            nom_nucleo: nomNucleo.trim() || null,
            num_parcela: numParcela.trim() || null,
            geom: fileState.geom,
            source_raw: fileState.sourceRaw,
          },
        });
        setSuccessWarnings(response.warnings ?? []);
        const centroid = approxCentroid(fileState.geom);
        onParcelaCreated({
          parcelaId: response.parcela_id,
          areaHa: response.area_ha,
          municipio,
          nomNucleo: nomNucleo.trim() || null,
          numParcela: numParcela.trim() || null,
          lng: centroid.lng,
          lat: centroid.lat,
        });
      }
    } catch (e) {
      if (e instanceof BffError) {
        setSubmitError(mapBffErrorToCopy(e, isReplace));
      } else if (e instanceof Error) {
        setSubmitError(e.message);
      } else {
        setSubmitError('Error desconocido.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    fileState.kind === 'parsed' &&
    (isReplace || municipio !== '') &&
    !submitting;

  const replacePrev =
    mode?.kind === 'replace' && typeof mode.areaHaPrevia === 'number'
      ? mode.areaHaPrevia
      : null;

  return (
    <div className="upload-parcela-privada glass-panel" style={containerStyle}>
      <div className="upload-pp-header">
        <strong style={{ display: 'block', marginBottom: 4 }}>
          {isReplace ? 'Reemplazar polígono del predio' : 'Cargar polígono privado'}
        </strong>
        <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
          {isReplace
            ? 'Arrastra el nuevo GeoJSON. Se mantiene el mismo identificador, propietario, gestión y documentos — solo cambia la geometría y el área.'
            : 'Arrastra el archivo del predio (GeoJSON, KML o KMZ). El sistema lo convierte a GeoJSON, valida la geometría, detecta solapes contra parcelas existentes y le asigna un identificador único.'}
        </span>
      </div>

      {isReplace && mode?.kind === 'replace' && (
        <div className="upload-pp-msg upload-pp-msg--warning" style={warningStyle}>
          <strong>Vas a reemplazar el polígono de {mode.parcelaId}.</strong>
          {replacePrev !== null && (
            <span style={{ display: 'block', marginTop: 2 }}>
              Área actual: {replacePrev.toFixed(2)} ha. Se recalculará al guardar.
            </span>
          )}
          <span style={{ display: 'block', marginTop: 2 }}>
            Esta acción NO afecta al propietario, la gestión, ni los documentos del expediente.
          </span>
        </div>
      )}

      <div
        className={`upload-inline${drag ? ' is-dragover' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e)  => { e.preventDefault(); setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
      >
        <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--teal)', flexShrink: 0 }}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div className="upload-inline-text">
          <strong>Suelta el archivo aquí</strong>
          <span>.geojson · .json · .kml · .kmz · máx 5 MB · Polygon/MultiPolygon (WGS84)</span>
        </div>
        <label className="btn-ghost upload-inline-btn" style={{ cursor: 'pointer' }}>
          Seleccionar
          <input
            type="file"
            accept=".geojson,.json,.kml,.kmz,application/geo+json,application/json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void handleFile(f); }}
          />
        </label>
      </div>

      {fileState.kind === 'error' && (
        <div className="upload-pp-msg upload-pp-msg--error" style={errorStyle}>
          {fileState.message}
        </div>
      )}

      {fileState.kind === 'parsed' && (
        <>
          <div className="upload-pp-preview" style={previewStyle}>
            <div>
              <strong style={{ fontSize: 13 }}>✓ {fileState.fileName}</strong>
              <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 2 }}>
                Geometría: {fileState.geom.type} · El área se calcula al guardar
              </div>
            </div>
            <button
              type="button"
              className="btn-ghost"
              style={{ fontSize: 11, padding: '4px 8px' }}
              onClick={() => setFileState({ kind: 'idle' })}
            >
              Cambiar archivo
            </button>
          </div>

          {fileState.warnings.map((w, i) => (
            <div key={i} className="upload-pp-msg upload-pp-msg--warning" style={warningStyle}>
              {w}
            </div>
          ))}

          {/* Campos de metadata: solo en modo `create`. En `replace`
              preservamos municipio/nom_nucleo/num_parcela del registro original. */}
          {!isReplace && (
            <>
              <div className="form-row" style={{ marginTop: 12 }}>
                <label>
                  <span>Municipio <small className="hint-mini">requerido</small></span>
                  <select
                    value={municipio}
                    onChange={(e) => setMunicipio(e.target.value as Municipio)}
                  >
                    <option value="">— selecciona —</option>
                    {MUNICIPIOS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Nombre del predio <small className="hint-mini">opcional</small></span>
                  <input
                    value={nomNucleo}
                    placeholder='Ej: "Rancho Familiar García"'
                    maxLength={200}
                    onChange={(e) => setNomNucleo(e.target.value)}
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="full">
                  <span>Número o referencia interna <small className="hint-mini">opcional</small></span>
                  <input
                    value={numParcela}
                    placeholder="Si el predio tiene un número catastral u otra referencia"
                    maxLength={40}
                    onChange={(e) => setNumParcela(e.target.value)}
                  />
                </label>
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            {isReplace && mode?.kind === 'replace' && mode.onCancel && (
              <button
                type="button"
                className="btn-ghost"
                disabled={submitting}
                onClick={() => mode.onCancel?.()}
              >
                Cancelar
              </button>
            )}
            <button
              type="button"
              className="btn-primary"
              disabled={!canSubmit}
              onClick={() => { void handleSubmit(); }}
            >
              {submitting
                ? (isReplace ? 'Reemplazando…' : 'Cargando…')
                : (isReplace ? 'Reemplazar polígono' : 'Cargar predio')}
            </button>
          </div>
        </>
      )}

      {submitError && (
        <div className="upload-pp-msg upload-pp-msg--error" style={errorStyle}>
          {submitError}
        </div>
      )}

      {successWarnings.length > 0 && (() => {
        const severe = successWarnings.filter(
          (w) => w.kind === 'contains_existing' || w.kind === 'overlap_exceeded',
        );
        const minor = successWarnings.filter(
          (w) => !w.kind || w.kind === 'minor',
        );
        return (
          <>
            {/* Solapes graves (> 1% o contención total). Tras la mig 0084
                el insert tampoco los bloquea — el técnico de campo necesita
                cargar incluso predios mal trazados. El bloque rojo los
                marca para que se vean diferentes de los minor (amarillos). */}
            {severe.length > 0 && (
              <div className="upload-pp-msg upload-pp-msg--error" style={errorStyle}>
                <strong>
                  {isReplace
                    ? 'Polígono reemplazado con solapes graves:'
                    : 'Predio creado con solapes graves:'}
                </strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  {severe.map((w, i) => {
                    const label = w.source === 'RAN' ? 'Parcela RAN' : 'Predio privado';
                    const tipoCopy = w.kind === 'contains_existing'
                      ? `contiene completamente a ${label} ${w.parcela_id}`
                      : `${Number(w.overlap_pct).toFixed(2)}% de solape con ${label} ${w.parcela_id}`;
                    return <li key={`severe-${i}`}>{tipoCopy}</li>;
                  })}
                </ul>
                <span style={{ display: 'block', marginTop: 4, fontSize: 11.5 }}>
                  {isReplace
                    ? 'Se guardó porque autorizaste el reemplazo. Revisa la geometría si fue un error.'
                    : 'Se cargó igual. Revisa la geometría con el responsable si fue un error.'}
                </span>
              </div>
            )}
            {minor.length > 0 && (
              <div className="upload-pp-msg upload-pp-msg--warning" style={warningStyle}>
                <strong>
                  {isReplace
                    ? 'Polígono reemplazado con solapes menores (≤ 1%):'
                    : 'Predio creado con solapes menores (≤ 1%):'}
                </strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  {minor.map((w, i) => (
                    <li key={`minor-${i}`}>
                      {w.source === 'RAN' ? 'Parcela RAN' : 'Predio privado'} {w.parcela_id}: {Number(w.overlap_pct).toFixed(2)}% de solape
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

function mapBffErrorToCopy(e: BffError, isReplace = false): string {
  const detailObj = typeof e.detail === 'object' && e.detail !== null ? (e.detail as Record<string, unknown>) : {};
  const code = typeof detailObj.error === 'string' ? detailObj.error : '';
  const detailStr = typeof detailObj.detail === 'string' ? detailObj.detail : '';
  switch (code) {
    case 'overlap_contains_existing':
      return 'El polígono cargado contiene completamente otra parcela existente. Revisa la geometría — probablemente es un duplicado.';
    case 'overlap_exceeded':
      return 'El polígono se solapa más del 1% con otra parcela existente (RAN o privada). Ajusta el polígono o consulta con el responsable.';
    case 'geom_invalid':
      return 'La geometría es inválida (self-intersección o área cero). Revisa el archivo y vuelve a intentar.';
    case 'invalid_tenant_claim':
      return 'No se pudo verificar tu sesión. Cierra sesión y vuelve a entrar.';
    case 'tenant_unknown':
      return 'Tu cuenta no está asociada a un tenant activo. Contacta al administrador.';
    case 'parcela_privada_not_found':
      return 'El predio ya no existe (puede haber sido eliminado). Refresca la página.';
    case 'validation':
      return isReplace
        ? 'Archivo GeoJSON inválido. Revisa que tenga Polygon o MultiPolygon.'
        : 'Datos del formulario inválidos. Revisa municipio y archivo.';
    case 'parcela_privada_insert_failed':
    case 'parcela_privada_replace_failed':
      // Caso desconocido: exponer el detail crudo del servidor para
      // diagnosticar. El detail tiene el mensaje del RPC con SQLSTATE y
      // contexto PL/pgSQL, lo que basta para arreglar sin acceso a logs.
      return `Error en servidor: ${detailStr || e.message}`;
    default:
      return `Error inesperado (${code || 'sin código'}): ${detailStr || e.message}`;
  }
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 16px',
  marginBottom: 2,
  borderRadius: 'var(--radius-md)',
};

const errorStyle: CSSProperties = {
  padding: '8px 12px',
  background: 'rgba(220, 80, 60, 0.10)',
  border: '1px solid rgba(220, 80, 60, 0.32)',
  borderRadius: 8,
  color: '#e87766',
  fontSize: 12.5,
  lineHeight: 1.45,
};

const warningStyle: CSSProperties = {
  padding: '8px 12px',
  background: 'rgba(218, 165, 32, 0.10)',
  border: '1px solid rgba(218, 165, 32, 0.32)',
  borderRadius: 8,
  color: '#d4a017',
  fontSize: 12.5,
  lineHeight: 1.45,
};

const previewStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 12px',
  background: 'rgba(180, 201, 111, 0.08)',
  border: '1px solid rgba(180, 201, 111, 0.32)',
  borderRadius: 8,
};
